import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import MeetingsPage from './pages/MeetingsPage';
import JoinMeetingPage from './pages/JoinMeetingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SignupPage from './pages/SignupPage';
import OnboardingPage from './pages/OnboardingPage';
import OnboardingWelcome from './pages/OnboardingWelcome';
import ChangelogPage from './pages/ChangelogPage';
import HelpPage from './pages/HelpPage';
import DevelopersPage from './pages/DevelopersPage';
import ProductTour from './components/ProductTour';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import DashboardPage from './pages/DashboardPage';
import VirtualOfficesPage from './pages/VirtualOfficesPage';
import ContractTemplatesPage from './pages/ContractTemplatesPage';
import CreateDocumentPage from './pages/CreateDocumentPage';
import ClientsPage from './pages/ClientsPage';
import DealsPage from './pages/DealsPage';
import DealRoomPage from './pages/DealRoomPage';
import DocumentsPage from './pages/DocumentsPage';
import MessagesPage from './pages/MessagesPage';
import SettingsPage from './pages/SettingsPage';
import DeveloperSettingsPage from './pages/DeveloperSettingsPage';
import InvitePage from './pages/InvitePage';
import TeamInvitePage from './pages/TeamInvitePage';
import TasksPage from './pages/TasksPage';
import ClientDetailPage from './pages/ClientDetailPage';
import LanguagePickerPage from './pages/LanguagePickerPage';
import SuperAdminPage from "./pages/SuperAdminPage";
import NotFoundPage from './pages/NotFoundPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ErrorBoundary from './components/ErrorBoundary';
import ConnectPage from './pages/ConnectPage';
import CompanyProfilePage from './pages/CompanyProfilePage';
import ProductsPage from './pages/ProductsPage';
import DirectoryPage from './pages/DirectoryPage';
import DirectoryProfilePage from './pages/DirectoryProfilePage';
import DirectoryRouter from './pages/DirectoryRouter';
import SEODirectoryPage from './pages/SEODirectoryPage';
import CompliancePage from './pages/CompliancePage';
import IntelligencePage from './pages/IntelligencePage';
import RFQPage from './pages/RFQPage';
import EventsPage from "./pages/EventsPage";
import InvoicesPage from './pages/InvoicesPage';
import AnalyticsPage from './pages/AnalyticsPage';
import TemplatesPage from './pages/TemplatesPage';
import GuestSignPage from './pages/GuestSignPage';
import QuickContractPage from './pages/QuickContractPage';
import MyDocumentsPage from './pages/MyDocumentsPage';
import DocumentViewerPage from './pages/DocumentViewerPage';
import SharedDocumentPage from './pages/SharedDocumentPage';
import UploadRequestPage from './pages/UploadRequestPage';

// Layout
import AppLayout from './components/layout/AppLayout';

// Components
import MobileInstallBanner from './components/MobileInstallBanner';
import NotificationPrompt from './components/NotificationPrompt';
import { NotificationProvider } from './components/Notifications';

// Auth context
import { AuthProvider, useAuth } from './context/AuthContext';

// i18n - Language Provider
import { LanguageProvider, useTranslation } from './i18n';

// Language utilities
import { getStoredLanguage } from './lib/languages';

import './App.css';

/**
 * LanguageSync component
 * Syncs language between AuthContext (user profile) and LanguageProvider (i18n)
 * This ensures that when user logs in, the app language updates to their preference
 * BUT only on initial login, not when user manually changes language
 */
function LanguageSync({ children }) {
  const { user, preferredLanguage } = useAuth();
  const { setLanguage, language } = useTranslation();
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Only sync on initial login, not on subsequent language changes
    if (hasInitialized.current) return;

    // When user logs in with a preferred language, sync it to i18n context
    if (user?.preferred_language) {
      setLanguage(user.preferred_language);
      hasInitialized.current = true;
    } else if (preferredLanguage) {
      // Fallback to auth context's preferredLanguage
      setLanguage(preferredLanguage);
      hasInitialized.current = true;
    }
  }, [user?.preferred_language, preferredLanguage, setLanguage]);

  return children;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex">
        {/* Sidebar skeleton */}
        <div className="hidden md:flex w-64 border-r bg-card flex-col p-4 gap-3">
          <div className="h-10 w-32 bg-muted rounded animate-pulse mb-4" />
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-8 bg-muted rounded animate-pulse" style={{ width: `${60 + Math.random() * 30}%` }} />
          ))}
        </div>
        {/* Main content skeleton */}
        <div className="flex-1 p-6 space-y-4">
          <div className="h-8 w-48 bg-muted rounded animate-pulse" />
          <div className="h-4 w-32 bg-muted rounded animate-pulse" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-card border rounded-lg animate-pulse" />
            ))}
          </div>
          <div className="h-64 bg-card border rounded-lg animate-pulse mt-4" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Check if user needs to choose a language (first login without preference)
  const hasLanguagePreference = user.preferred_language || getStoredLanguage();
  if (!hasLanguagePreference && location.pathname !== '/welcome') {
    return <Navigate to="/welcome" state={{ redirectTo: location.pathname }} />;
  }

  return children;
}

function AppRoutes() {
  return (
    <>
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/welcome" element={<LanguagePickerPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/meet/:joinCode" element={<JoinMeetingPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/team-invite/:token" element={<TeamInvitePage />} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/connect/:token" element={<ConnectPage />} />
              <Route path="/sign/:token" element={<GuestSignPage />} />
              <Route path="/shared/doc/:token" element={<SharedDocumentPage />} />
              <Route path="/upload-request/:token" element={<UploadRequestPage />} />

      {/* Protected routes (no app layout) */}
      <Route path="/onboarding" element={
        <PrivateRoute>
          <OnboardingPage />
        </PrivateRoute>
      } />
      <Route path="/onboarding/welcome" element={
        <PrivateRoute>
          <OnboardingWelcome />
        </PrivateRoute>
      } />
      <Route path="/changelog" element={
        <PrivateRoute>
          <ChangelogPage />
        </PrivateRoute>
      } />
      <Route path="/help" element={
        <PrivateRoute>
          <HelpPage />
        </PrivateRoute>
      } />
      <Route path="/developers" element={
        <PrivateRoute>
          <DevelopersPage />
        </PrivateRoute>
      } />

      {/* App routes with layout */}
      <Route element={
        <PrivateRoute>
          <AppLayout />
        </PrivateRoute>
      }>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/offices" element={<VirtualOfficesPage />} />
        <Route path="/virtual-offices" element={<VirtualOfficesPage />} />
        {/* Team is now inside Company Profile */}
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/:clientId" element={<ClientDetailPage />} />
        <Route path="/meetings" element={<MeetingsPage />} />
              <Route path="/deals" element={<DealsPage />} />
        <Route path="/deal/:dealId" element={<DealRoomPage />} />
        <Route path="/deals/:dealId" element={<DealRoomPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/my-documents" element={<MyDocumentsPage />} />
        <Route path="/documents/:id" element={<DocumentViewerPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/developer-settings" element={<DeveloperSettingsPage />} />
        <Route path="/company-profile" element={<CompanyProfilePage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/directory" element={<DirectoryPage />} />
        <Route path="/directory/:slug/:country" element={<SEODirectoryPage />} />
        <Route path="/directory/:slug" element={<DirectoryRouter />} />
        <Route path="/rfq" element={<RFQPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/compliance" element={<CompliancePage />} />
        <Route path="/intelligence" element={<IntelligencePage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/contract-templates" element={<ContractTemplatesPage />} />
        <Route path="/documents/create" element={<CreateDocumentPage />} />
        <Route path="/quick-contract" element={<QuickContractPage />} />
        <Route path="/superadmin" element={<SuperAdminPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    <ProductTour />
    <KeyboardShortcuts />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <AuthProvider>
            <NotificationProvider>
              <LanguageSync>
                <ErrorBoundary><Router>
                  <AppRoutes />
                  <Toaster position="top-right" />
                  <MobileInstallBanner />
                  <NotificationPrompt />
                </Router></ErrorBoundary>
              </LanguageSync>
            </NotificationProvider>
          </AuthProvider>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
